import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  H1,
  Typography,
  Card as BaseCard,
  Checkbox,
  Button as BaseButton,
  Alert as BaseAlert
} from '../components';
import { colors, Elevation, getSpacing } from '../utils';
import { signJobAgentSow as signJobAgentSowAction } from '../lib/redux/actions/me';
import { ROUTES } from '../Constants';
import { gotoDashboard as gotoDashboardAction } from '../lib/redux/actions/dashboard';
import iconWarning from '../assets/images/icon-warning.svg';
import { setAvailable } from '../lib/taskrouter';

const Button = styled(BaseButton)`
  flex: 1;
  margin-right: 15px;

  &:last-child {
    margin-right: 0;
  }
`;

const Card = styled(BaseCard)`
  width: 581px;
  margin-top: ${getSpacing('medium')};
`;

const Title = styled(H1)`
  margin-bottom: 16px;
  font-weight: bold;
  font-size: 24px;
`;

const Subtitle = styled(Typography)`
  text-align: center;
  margin-bottom: 16px;
`;

const Text = styled(Typography)`
  text-align: left;
  margin-bottom: 24px;
`;

const SowItem = styled.div`
  align-self: start;
  margin-bottom: 24px;
  text-align: start;
  display: flex;
  flex-direction: column;
  color: ${colors.g05};

  span.label {
    font-weight: bold;
  }
`;

const SowDateWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin: -${getSpacing('small')} 0 ${getSpacing('medium')} -${getSpacing('small')};
  width: calc(100% + ${getSpacing('small')});

  .sow-date {
    border-radius: 4px;
    background-color: ${colors.g01};
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 60px;
    margin: ${getSpacing('small')} 0 0 ${getSpacing('small')};
  }
  span {
    color: ${colors.g05};
  }
  .label {
    font-weight: bold;
  }
`;

const Padded = styled.div`
  margin: 20px 0;
  width: 100%;
  display: flex;
  align-items: center;
`;

const Alert = styled(BaseAlert)`
  left: 0;
  right: 0;
  top: 72px;
  background-color: ${colors.o01}10;
  color: ${colors.g04};
  display: block;
  text-align: left;
  padding-right: ${getSpacing('xs')};
  margin-top: ${getSpacing('medium')};
  z-index: ${Elevation.Alert};
`;

const AlertIcon = styled.img`
  display: inline-block;
  vertical-align: middle;
`;

export const Sow = ({ jobSow, signJobAgentSow, gotoDashboard, history, location }) => {
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isSigning, setIsSigning] = useState(false);

  const onClick = async () => {
    setIsSigning(true);
    const jobRoute =
      location.state && location.state.nextRoute ? location.state.nextRoute : ROUTES.JOB;

    await setAvailable();

    signJobAgentSow(jobSow.jobId, jobSow.sowId)
      .then(() => history.push(jobRoute))
      .catch(() => setIsSigning(false));
  };

  const onExitClick = async () => {
    await gotoDashboard(history);
  };

  const { isUpdated, agentSowJson, jobTypeName, jobCode } = jobSow;
  const detailsKeys = Object.keys(agentSowJson.details);

  const sowDetails = detailsKeys.map(key => {
    return agentSowJson.details[key] ? (
      <SowItem key={`sowItem_${key}`}>
        <span className="label">{key}</span>
        <span>{agentSowJson.details[key]}</span>
      </SowItem>
    ) : null;
  });

  return (
    <div>
      {isUpdated && (
        <Alert>
          <AlertIcon src={iconWarning} alt="SOW agreement update warning" />
          This job SOW has been updated. Read and if you are in agreement, accept the SOW.
        </Alert>
      )}
      <Card>
        <Title>Agent Agreement</Title>
        <Subtitle>
          Scope of Work (“SOW”) of
          <br />
          {jobTypeName} | ID: {jobCode}
        </Subtitle>
        <Text>
          This SOW (ID: {jobCode}) as part of the Neevo Community Member Agreement (“Agreement”) and
          our Terms of Use (“ToU”) sets forth the terms and conditions of services the undersigned
          Neevo Community Member as an Independent Contractor (“You” or “Your”) will perform for
          DefinedCrowd Corporation and its affiliates, subsidiaries and agents (the “Company”)
          (together, the “Parties”). The Parties have agreed to, and executed, the Agreement on the
          Effective Date set forth therein in contemplation of the completion of distinct services,
          each described with particularity in its respective “SOW (ID: {jobCode})”. The execution
          of each respective and subsequent SOW allows the Parties to conduct business without the
          necessity of renegotiating and approving the terms in the Agreement with each new project.
          The Parties agree to continue to comply with the terms of the Agreement that are not
          addressed in SOW (ID: {jobCode}).
        </Text>

        <SowDateWrapper>
          <div className="sow-date">
            <span className="label">Start Date</span>
            <span>{agentSowJson.beginDate}</span>
          </div>
          <div className="sow-date">
            <span className="label">End Date</span>
            <span>{agentSowJson.endDate}</span>
          </div>
        </SowDateWrapper>

        {sowDetails}

        <Checkbox
          data-test="terms-checkbox"
          style={{ marginTop: 0 }}
          onToggle={() => setTermsAgreed(!termsAgreed)}
          checked={termsAgreed}
          label={`Checking this box indicates you agree with the terms set forth in the 
          Company’s Privacy Policy, Terms of Use, the Neevo Community Member Agreement, 
          the SOW (ID: ${jobCode}) and any amendments thereto (“<strong>Controlling Documents</strong>.”).`}
        />

        <Padded>
          <Button data-test="exit-button" theme="bordered" onClick={onExitClick}>
            Exit job
          </Button>
          <Button data-test="accept-button" disabled={!termsAgreed || isSigning} onClick={onClick}>
            Accept terms
          </Button>
        </Padded>
      </Card>
    </div>
  );
};

Sow.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  jobSow: PropTypes.shape({
    jobId: PropTypes.string,
    sowId: PropTypes.string,
    isSigned: PropTypes.bool,
    isUpdated: PropTypes.bool,
    agentSowJson: PropTypes.object,
    jobTypeName: PropTypes.string,
    jobCode: PropTypes.string
  }),
  signJobAgentSow: PropTypes.func.isRequired,
  gotoDashboard: PropTypes.func.isRequired,
  location: PropTypes.shape({
    state: PropTypes.object
  }).isRequired
};

Sow.defaultProps = {
  jobSow: {
    agentSowJson: {}
  }
};

function mapStateToProps({ me }) {
  return {
    jobSow: me.jobSow
  };
}

export default connect(
  mapStateToProps,
  {
    signJobAgentSow: signJobAgentSowAction,
    gotoDashboard: gotoDashboardAction
  }
)(withRouter(Sow));
